import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'

import type { RegisterUserPayload } from './types'

function getUser(): RegisterUserPayload {
  const saved = localStorage.getItem('user')
  return saved ? JSON.parse(saved) : { userID: null }
}

function saveUser(newUser: RegisterUserPayload) {
  const existing = getUser()
  const updated = { ...existing, ...newUser }
  localStorage.setItem('user', JSON.stringify(updated))
}

export const useUserStore = defineStore('userStore', () => {
  const savedUser = getUser()
  const userID = ref(savedUser?.userID)

  const roles = ref(savedUser?.roles ?? [])

  const email = ref(savedUser?.email)
  const firma = ref(savedUser?.firma)
  const vorname = ref(savedUser?.vorname)
  const nachname = ref(savedUser?.nachname)
  const avatar = ref(savedUser?.avatar)
  const isMultiAccountUser = ref(savedUser?.isMultiAccountUser)
  const admin = ref(savedUser?.admin)

  function registerUser(user: RegisterUserPayload): void {
    const combinedUser = { ...getUser(), ...user }

    userID.value = combinedUser.userID
    roles.value = combinedUser.roles ?? []
    firma.value = combinedUser.firma
    email.value = combinedUser.email
    vorname.value = combinedUser.vorname
    nachname.value = combinedUser.nachname
    avatar.value = combinedUser.avatar
    isMultiAccountUser.value = combinedUser.isMultiAccountUser
    admin.value = combinedUser.admin

    saveUser(combinedUser)
  }

  function updateUser(partialUser: Partial<RegisterUserPayload>): void {
    const currentUser = getUser()
    const updatedUser = { ...currentUser, ...partialUser }

    // Only update provided fields
    if (partialUser.userID !== undefined) userID.value = partialUser.userID
    if (partialUser.roles !== undefined) roles.value = partialUser.roles
    if (partialUser.firma !== undefined) firma.value = partialUser.firma
    if (partialUser.email !== undefined) email.value = partialUser.email
    if (partialUser.vorname !== undefined) vorname.value = partialUser.vorname
    if (partialUser.nachname !== undefined) nachname.value = partialUser.nachname
    if (partialUser.avatar !== undefined) avatar.value = partialUser.avatar
    if (partialUser.isMultiAccountUser !== undefined)
      isMultiAccountUser.value = partialUser.isMultiAccountUser
    if (partialUser.admin !== undefined) admin.value = partialUser.admin

    saveUser(updatedUser)
  }

  function removeUser(): void {
    userID.value = null
    roles.value = []
    firma.value = null
    email.value = null
    vorname.value = null
    nachname.value = null
    avatar.value = null
    isMultiAccountUser.value = null
    admin.value = null
    localStorage.removeItem('user')
  }

  return {
    userID: readonly(userID),
    roles: readonly(roles),
    firma: readonly(firma),
    email: readonly(email),
    vorname: readonly(vorname),
    nachname: readonly(nachname),
    avatar: readonly(avatar),
    isMultiAccountUser: readonly(isMultiAccountUser),
    admin,
    registerUser,
    updateUser,
    removeUser,
  }
})
